import { useContext, useState } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { Separator } from '@givebutter/primitives/separator';
import { faUserPen } from '@fortawesome/pro-regular-svg-icons';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { CrmMemberComboboxItem } from 'components/tasks/CrmMemberComboboxItem';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import useDebounce from 'customHooks/useDebounce';
import { useIsPending } from 'customHooks/useIsPending';
import Logo from 'images/logo-horizontal.svg?react';
import { identity } from 'lodash';
import { CrmMember } from 'types/domain';

interface CrmMemberFilterFormValues extends FilterFormValue<string[]> {}

export const filterKey: ServerKey = 'created_by';
export const Label: LabelFilterComponent = () => 'Creator';
export const Icon: FilterIcon = faUserPen;

export const Filter: ContentFilterComponent = () => {
    const [search, setSearch] = useState('');
    const { resource } = useContext(FilterContext);
    const debouncedSearch = useDebounce(search);
    const currentFilter = useCurrentFilter<(string | number)[]>();

    const [isPending, params] = useIsPending(
        {
            query: debouncedSearch || undefined,
            limit: 9999,
        },
        500,
    );

    const { data: members, isFetching } = useCrmMembers(params, resource === 'reports');
    const selectedMembers = members.filter(m => currentFilter?.value.includes(m.id));

    const filterContext = useFilter<number[], string[]>({
        formValueToFilter: con => con.map(c => parseInt(c)),
    });

    const methods = Form.useForm<CrmMemberFilterFormValues>({
        defaultValues: {
            value: selectedMembers.map(c => c.id.toString()),
        },
    });
    const { control, path } = methods;

    return (
        <LoadingOverlay size={'sm'} isLoading={isPending} delay={2000}>
            <FilterFormContext.Provider value={filterContext}>
                <Form.Provider {...methods}>
                    <FilterForm>
                        <FilterContainer>
                            <Form.Container>
                                <Form.Label className="sr-only" name={path('value')}>
                                    Creators
                                </Form.Label>
                                <Form.Combobox
                                    control={control}
                                    name="value"
                                    rules={{ required: ValidationTextMap.Required }}
                                >
                                    <Combobox.Content>
                                        <Combobox.Search
                                            placeholder="Search"
                                            value={search}
                                            onChangeValue={setSearch}
                                            isLoading={isFetching}
                                        />
                                        <FilterComboboxItems>
                                            {selectedMembers.map(item => (
                                                <CrmMemberItem key={item.id} item={item} />
                                            ))}
                                            {selectedMembers.length > 0 && (
                                                <Separator className={'my-1'} />
                                            )}
                                            {members.map(item => {
                                                if (selectedMembers.find(c => c.id === item.id)) {
                                                    return null;
                                                }
                                                return <CrmMemberItem key={item.id} item={item} />;
                                            })}
                                        </FilterComboboxItems>
                                    </Combobox.Content>
                                </Form.Combobox>
                                <Form.Error name={path('value')} />
                            </Form.Container>
                            <FilterSubmit />
                        </FilterContainer>
                    </FilterForm>
                </Form.Provider>
            </FilterFormContext.Provider>
        </LoadingOverlay>
    );
};

const CrmMemberItem = ({ item }: { item: CrmMember }) => {
    if (item.id === -1) {
        return (
            <Combobox.Item value={item.id.toString()}>
                <Combobox.CheckboxIndicator />
                <Combobox.ItemText>
                    <Logo title="Givebutter" />
                </Combobox.ItemText>
            </Combobox.Item>
        );
    }

    return <CrmMemberComboboxItem member={item} key={item.id} />;
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<number[]>();
    const { resource } = useContext(FilterContext);
    const { isPending, data: members } = useCrmMembers(undefined, resource === 'reports');
    const selectedMembers = members.filter(m => currentFilter?.value.includes(m.id));

    const valueStr = transformMultiselectToReadable(selectedMembers, {
        transform: c => `${c.first_name} ${c.last_name}`,
        cutoffLength: 1,
    });

    return (
        <LoadingOverlay isLoading={isPending} size={'sm'} delay={5000}>
            <ReadableFilterValue>{valueStr}</ReadableFilterValue>
        </LoadingOverlay>
    );
};

export const downstream: DownstreamFilterTransform<CrmMember> = identity;

export const upstream: UpstreamFilterTransform<CrmMember> = (filter: FilterValue) =>
    injectEquals({
        value: filter.value,
    });
